import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { Button, Dropdown } from 'antd';
import { FC, useMemo } from 'react';
import * as Icon from 'react-feather';
import {
  ClassifierFormDataType,
  CategoryFormDataType,
  CategoryMappingType,
  ClassifierMappingsType,
  DropdownItem,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import styled from 'styled-components';
import translations from './ClassifierMappingActions.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'classifier-mapping-actions'
);
addTranslations(translations);

interface ClassifierMappingActionsProps {
  classifier?: ClassifierFormDataType;
}

interface CategoryMappingDropdownProps {
  categories: CategoryFormDataType[];
  categoryIdFromType: string;
}

function addMissing(
  categoryMappings: CategoryMappingType[],
  { categories }: CategoryMappingDropdownProps
): CategoryMappingType[] {
  const existing = new Set(categoryMappings.map((cm) => cm.categoryIdFrom));

  return [
    ...categoryMappings,
    ...categories
      .filter((category) => category.id && !existing.has(category.id))
      .map((category) => {
        return {
          categoryIdFrom: category.id ?? 0,
        } as CategoryMappingType;
      }),
  ];
}

const CategoryMappingDropdown: FC<CategoryMappingDropdownProps> = (props) => {
  const form = useFormContext<ClassifierMappingsType>();
  const { t } = useTranslation();

  return (
    <DropdownItem
      onClick={() => {
        const categoryMappings = form.getValues()['categoryMappings'];
        form.setValue('categoryMappings', addMissing(categoryMappings, props), {
          shouldDirty: true,
        });
      }}
    >
      {t(`dropdown.${props.categoryIdFromType}`)}
    </DropdownItem>
  );
};

function makeDropdownOption(props: CategoryMappingDropdownProps) {
  return {
    key: props.categoryIdFromType,
    label: <CategoryMappingDropdown {...props} />,
  };
}

export function ClassifierMappingActions({
  classifier,
}: ClassifierMappingActionsProps) {
  const { t } = useTranslation();

  const items = useMemo(() => {
    return [
      makeDropdownOption({
        categories: classifier?.categories ?? [],
        categoryIdFromType: 'category',
      }),
      makeDropdownOption({
        categories: classifier?.categoryGroups ?? [],
        categoryIdFromType: 'category_group',
      }),
      makeDropdownOption({
        categories: classifier?.combinedGroups ?? [],
        categoryIdFromType: 'combined_group',
      }),
    ];
  }, [classifier]);

  return (
    <Dropdown disabled={!classifier} menu={{ items }}>
      <StyledButton id="add-all" icon={<Icon.Plus size={18} />} size={'middle'}>
        {t('button.addAll')}
      </StyledButton>
    </Dropdown>
  );
}

const StyledButton = styled(Button)`
  .ant-btn-icon {
    height: 18px;
  }
`;
